import React, { useState, useEffect, memo } from 'react';
import { MomentData, LinkedMoment } from '../types';
import MetaRow from './MetaRow';
import MilestoneRow from './MilestoneRow';

interface TableBodyProps {
  data: MomentData[];
  columnsConfig: any[];
  type: 'meta' | 'milestones';
  hoveredRow: string | null;
  expandedRows: Set<string>;
  rowHoveredClass: string;
  onRowHover: (issueId: string) => void;
  onRowLeave: () => void;
  onToggleRow: (issueId: string) => void;
  // Shared chunking state
  renderedChunks?: number;
  setRenderedChunks?: (value: number | ((prev: number) => number)) => void;
  isLoadingMore?: boolean;
  setIsLoadingMore?: (value: boolean) => void;
}

const CHUNK_SIZE = 50;
const CHUNK_DELAY = 16; // Delay in ms between chunks (~1 frame at 60fps for smooth rendering)

const TableBody: React.FC<TableBodyProps> = memo(
  ({
    data,
    columnsConfig,
    type,
    hoveredRow,
    expandedRows,
    rowHoveredClass,
    onRowHover,
    onRowLeave,
    onToggleRow,
    renderedChunks: externalRenderedChunks,
    setRenderedChunks: externalSetRenderedChunks,
    isLoadingMore: externalIsLoadingMore,
    setIsLoadingMore: externalSetIsLoadingMore
  }) => {
    // Use local state if external state is not provided (for backward compatibility)
    const [localRenderedChunks, setLocalRenderedChunks] = useState<number>(1);
    const [localIsLoadingMore, setLocalIsLoadingMore] = useState(false);

    const renderedChunks = externalRenderedChunks ?? localRenderedChunks;
    const setRenderedChunks =
      externalSetRenderedChunks ?? setLocalRenderedChunks;
    const isLoadingMore = externalIsLoadingMore ?? localIsLoadingMore;
    const setIsLoadingMore = externalSetIsLoadingMore ?? setLocalIsLoadingMore;

    // Reset chunks when data changes
    useEffect(() => {
      setRenderedChunks(1);
    }, [data, setRenderedChunks]);

    // Chunk rendering effect
    useEffect(() => {
      const totalChunks = Math.ceil(data.length / CHUNK_SIZE);

      if (renderedChunks < totalChunks && !isLoadingMore) {
        setIsLoadingMore(true);

        // Add delay to make progressive loading visible (for testing)
        setTimeout(() => {
          setRenderedChunks(prev => prev + 1);
          setIsLoadingMore(false);
        }, CHUNK_DELAY);
      }
    }, [renderedChunks, data.length, isLoadingMore]);

    // Calculate how many rows to render
    const rowsToRender = data.slice(0, renderedChunks * CHUNK_SIZE);
    const renderRow = (
      moment: MomentData | LinkedMoment,
      isLinked: boolean = false
    ) => {
      if (type === 'meta') {
        return (
          <MetaRow
            moment={moment}
            isLinked={isLinked}
            columnsConfig={columnsConfig}
            hoveredRow={hoveredRow}
            expandedRows={expandedRows}
            rowHoveredClass={rowHoveredClass}
            onRowHover={onRowHover}
            onRowLeave={onRowLeave}
            onToggleRow={onToggleRow}
          />
        );
      }

      return (
        <MilestoneRow
          moment={moment}
          isLinked={isLinked}
          columnsConfig={columnsConfig}
          hoveredRow={hoveredRow}
          rowHoveredClass={rowHoveredClass}
          onRowHover={onRowHover}
          onRowLeave={onRowLeave}
        />
      );
    };

    const totalRows = data.length;
    const hasMoreToLoad = renderedChunks * CHUNK_SIZE < totalRows;

    return (
      <tbody>
        {rowsToRender.map(moment => (
          <React.Fragment key={`${moment.issueId}-${moment.milestoneId}`}>
            {renderRow(moment)}
            {moment.linkedMoments &&
              expandedRows.has(moment.issueId) &&
              moment.linkedMoments.map(linkedMoment => (
                <React.Fragment
                  key={`${moment.issueId}-linkedMoment-${linkedMoment.milestoneId}`}
                >
                  {renderRow(linkedMoment, true)}
                </React.Fragment>
              ))}
          </React.Fragment>
        ))}
        {hasMoreToLoad && (
          <tr>
            <td
              colSpan={columnsConfig.length}
              style={{
                textAlign: 'center',
                padding: '20px',
                backgroundColor: '#f5f5f5',
                fontWeight: 'bold',
                color: '#147af3'
              }}
            >
              ⏳ Loading more rows... ({rowsToRender.length} of {totalRows})
            </td>
          </tr>
        )}
      </tbody>
    );
  }
);

TableBody.displayName = 'TableBody';

export default TableBody;
